import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'antd/lib/button';

import { useRouter } from 'utils';
import { MapStyleFormContext } from 'contexts/MapStyleFormContext';
import { VISUALISATION_TYPES } from 'utils/constants/visualisationTypes';

import { BaseMap, BoundaryWidth, ShapeColor, VisualisationType } from './FormItems';
import ChoroplethOptions from './ChoroplethOptions';
import HeatmapOptions from './HeatmapOptions';

import { MapCustomizationFormService, createMutationVariables } from './service';
import { SpaceWrapper, ButtonContainer, SectionHeading } from './styles';


const { CHOROPLETH, HEATMAP, LOCATION } = VISUALISATION_TYPES;
const withDefault = (value, fallback) => (value === null || value === undefined ? fallback : value);

const MapCustomizationForm = ({ closeModal }) => {
  const { pageId } = useRouter();
  const MapStyleFormData = useContext(MapStyleFormContext);

  const {
    basemap,
    shapeBorderColor,
    shapeBorderWidth,
    visualizationType,
    aggregates,
    aggregatableFields,
    heatmapWeightField,
    heatmapGradient,
    heatmapContrast,
    heatmapVisibility,
  } = MapStyleFormData;
  const visualizedAggregate = aggregates.find(({ isVisualized }) => isVisualized);
  const initialVisualizationType = (
    visualizationType === CHOROPLETH && !visualizedAggregate
      ? LOCATION
      : visualizationType
  );

  const [gradient, changeGradient] = useState(heatmapGradient);
  const [showMidpoint, toggleMidpoint] = useState(Boolean(gradient.midpointValue));
  const [stateBasemap, onChangeBasemap] = useState(basemap);
  const [pickedColor, pickColor] = useState(shapeBorderColor);
  const [pickedBoundaryWidth, changeBoundaryWidth] = useState(withDefault(shapeBorderWidth, 2));
  const [weightIntensity, changeWeightIntensity] = useState(heatmapWeightField);
  const [contrast, changeContrast] = useState(withDefault(heatmapContrast, 50));
  const [visibility, changeVisibility] = useState(withDefault(heatmapVisibility, 50));
  const [selectedAggregateId, changeSelectedAggregateId] = useState(
    visualizedAggregate && visualizedAggregate.id,
  );
  const [choroplethError, setChoroplethError] = useState('');
  const [stateVisualizationType, setVisualizationType] = useState(initialVisualizationType);
  const handleVisualizationTypeChange = (nextVisualizationType) => {
    setVisualizationType(nextVisualizationType);
    if (nextVisualizationType !== CHOROPLETH) {
      setChoroplethError('');
    }
  };

  const { updateMap } = MapCustomizationFormService(pageId);

  const handleUpdateMapStyles = () => {
    if (stateVisualizationType === CHOROPLETH && !selectedAggregateId) {
      setChoroplethError('Select an insight to use choropleth maps.');

      return;
    }

    const variables = createMutationVariables({
      pageId,
      gradient,
      pickedColor,
      pickedBoundaryWidth,
      showMidpoint,
      stateBasemap,
      weightIntensity,
      heatmapContrast: contrast,
      heatmapVisibility: visibility,
      selectedAggregateId,
      stateVisualizationType,
    });
    closeModal();
    updateMap(variables);
  };

  return (
    <>
      <SpaceWrapper bottom={15}>
        <BaseMap
          basemap={basemap}
          onChangeBasemap={onChangeBasemap}
        />
      </SpaceWrapper>
      <SpaceWrapper bottom={30}>
        <VisualisationType
          aggregates={aggregates}
          visualizationType={stateVisualizationType}
          onChangeVisualizationType={handleVisualizationTypeChange}
        />
      </SpaceWrapper>
      <SpaceWrapper bottom={30}>
        <ShapeColor initialColor={pickedColor} pickColor={pickColor} />
      </SpaceWrapper>
      <SpaceWrapper bottom={30}>
        <BoundaryWidth value={pickedBoundaryWidth} onChange={changeBoundaryWidth} />
      </SpaceWrapper>
      {stateVisualizationType === HEATMAP && (
        <>
          <SectionHeading>Heatmap Options</SectionHeading>
          <HeatmapOptions
            gradient={gradient}
            showMidpoint={showMidpoint}
            changeGradient={changeGradient}
            toggleMidpoint={toggleMidpoint}
            heatmapWeightField={heatmapWeightField}
            heatmapContrast={contrast}
            heatmapVisibility={visibility}
            aggregatableFields={aggregatableFields}
            changeWeightIntensity={changeWeightIntensity}
            changeContrast={changeContrast}
            changeVisibility={changeVisibility}
          />
        </>
      )}
      {stateVisualizationType === CHOROPLETH && (
        <>
          <SectionHeading>Choropleth Options</SectionHeading>
          <SpaceWrapper bottom={20}>
            <ChoroplethOptions
              aggregates={aggregates}
              selectedAggregateId={selectedAggregateId}
              onChangeAggregate={(value) => {
                changeSelectedAggregateId(value);
                setChoroplethError('');
              }}
            />
          </SpaceWrapper>
          <SpaceWrapper bottom={15}>
            <p>
              Use the same gradient controls as heatmap mode to style low, mid and high choropleth values.
            </p>
          </SpaceWrapper>
          <HeatmapOptions
            gradient={gradient}
            showMidpoint={showMidpoint}
            changeGradient={changeGradient}
            toggleMidpoint={toggleMidpoint}
            heatmapWeightField={heatmapWeightField}
            heatmapContrast={contrast}
            heatmapVisibility={visibility}
            aggregatableFields={aggregatableFields}
            changeWeightIntensity={changeWeightIntensity}
            changeContrast={changeContrast}
            changeVisibility={changeVisibility}
            showWeightIntensity={false}
            showContrast={false}
            showVisibility={false}
          />
          {choroplethError && <p>{choroplethError}</p>}
        </>
      )}
      <ButtonContainer>
        <Button type="primary" onClick={handleUpdateMapStyles}>Update map</Button>
      </ButtonContainer>
    </>
  );
};

MapCustomizationForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default React.memo(MapCustomizationForm);
